import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../context/LanguageContext';
import { getCurrentMealPlan, generateMealPlanAsync, getMealPlanStatus } from '../lib/api';
import { usePremiumStatus } from '../lib/queries';
import { toast } from 'sonner';
import {
  ArrowLeft, Crown, Loader2, Sparkles, RefreshCw,
  ChevronDown, ChevronUp, Flame, Lock, AlertTriangle, Globe,
  Pencil, CookingPot, ShoppingCart
} from 'lucide-react';

function GeneratingTimer() {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setSeconds(s => s + 1), 1000);
    return () => clearInterval(interval);
  }, []);
  const progress = Math.min(seconds / 25 * 100, 95);
  const messages = [
    'Analisi del tuo profilo...',
    'Calcolo fabbisogno calorico...',
    'Selezione ingredienti...',
    'Creazione colazioni...',
    'Creazione pranzi...',
    'Creazione cene...',
    'Bilanciamento nutrizionale...',
    'Quasi pronto...'
  ];
  const msgIdx = Math.min(Math.floor(seconds / 3), messages.length - 1);
  return (
    <div className="w-full mt-4">
      <div className="w-full bg-gray-200 rounded-full h-2 mb-2 overflow-hidden">
        <div className="bg-gradient-to-r from-emerald-500 to-teal-500 h-full rounded-full transition-all duration-1000" style={{ width: `${progress}%` }} />
      </div>
      <p className="text-xs text-gray-400 animate-pulse">{messages[msgIdx]}</p>
    </div>
  );
}

export default function MealPlan() {
  const { isDark } = useTheme();
  const { t, language } = useTranslation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [mealPlan, setMealPlan] = useState(null);
  const [hasPlan, setHasPlan] = useState(false);
  const [planStatus, setPlanStatus] = useState(null); // 'generating' | 'ready' | 'failed' | null
  // Premium status — shared React Query hook (dedup'd with Dashboard, Profilo, Premium).
  const { data: premiumData, isSuccess: premiumLoaded } = usePremiumStatus();
  const isPremium = premiumData?.is_premium || false;
  const [expandedDay, setExpandedDay] = useState(0);
  const [error, setError] = useState('');
  const [allergies, setAllergies] = useState('');
  const [preferences, setPreferences] = useState('');
  const [showConfig, setShowConfig] = useState(false);
  const [planLanguage, setPlanLanguage] = useState('');
  const [planGlp1Support, setPlanGlp1Support] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [activePlanId, setActivePlanId] = useState(null);

  const pollActiveRef = useRef(false);

  const stopPolling = useCallback(() => {
    pollActiveRef.current = false;
  }, []);

  const startPolling = useCallback((planId) => {
    pollActiveRef.current = true;
    
    const poll = async () => {
      if (!pollActiveRef.current) return;
      try {
        const res = await getMealPlanStatus(planId);
        const data = res.data || res;
        if (data.status === 'ready') {
          pollActiveRef.current = false;
          setMealPlan(data.plan);
          setPlanStatus('ready');
          setPlanLanguage(data.language || language);
          setPlanGlp1Support(!!data.glp1_support);
          setAllergies(data.allergies || '');
          setPreferences(data.preferences || '');
          setHasPlan(true);
          setGenerating(false);
          setShowConfig(false);
          setExpandedDay(0);
          toast.success(t('mealPlan.readyNotification'));
          return;
        } else if (data.status === 'failed') {
          pollActiveRef.current = false;
          setPlanStatus('failed');
          setGenerating(false);
          setError(t('mealPlan.generateError'));
          return;
        }
      } catch (err) {
        console.warn('Meal plan poll error:', err);
      }
      if (pollActiveRef.current) {
        setTimeout(poll, 2000);
      }
    };

    // First poll after 1.5 seconds
    setTimeout(poll, 1500);
  }, [language, t]);

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const loadData = useCallback(async () => {
    try {
      const planRes = await getCurrentMealPlan();

      const data = planRes.data;
      if (data?.has_plan) {
        setHasPlan(true);
        setActivePlanId(data.plan_id);
        setPlanLanguage(data.language || 'it');
        setPlanGlp1Support(!!data.glp1_support);
        setAllergies(data.allergies || '');
        setPreferences(data.preferences || '');

        if (data.status === 'generating' || data.status === 'queued') {
          setPlanStatus('generating');
          setGenerating(true);
          startPolling(data.plan_id);
        } else if (data.status === 'ready' && data.plan) {
          setPlanStatus('ready');
          setMealPlan(data.plan);
          setGenerating(false);
          setShowConfig(false);
          setExpandedDay(0);
        } else if (data.status === 'failed') {
          setPlanStatus('failed');
          setGenerating(false);
        }
      } else {
        // No plan — reset all states
        setGenerating(false);
        setPlanStatus(null);
      }
    } catch (err) {
      console.error('MealPlan load error:', err);
      setGenerating(false);
    } finally {
      setLoading(false);
    }
  }, [startPolling]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    setPlanStatus('generating');
    try {
      // Enqueue on the task queue — returns in <100ms with plan_id + status=queued
      const res = await generateMealPlanAsync(language, allergies, preferences);
      const data = res.data || res;

      setActivePlanId(data.plan_id);
      setPlanLanguage(data.language || language);
      setHasPlan(true);
      setShowConfig(false);

      // Poll /status until ready (same flow as when user reopens the page mid-generation)
      startPolling(data.plan_id);
    } catch (err) {
      setGenerating(false);
      setPlanStatus(null);
      if (err.response?.status === 403) {
        setError(t('mealPlan.premiumRequired'));
      } else {
        setError(t('mealPlan.generateError'));
      }
    }
  };

  const handleEdit = () => {
    setShowConfig(true);
    setDisclaimerAccepted(true); // Already accepted before
  };

  // Meal type emoji fallback
  const getMealEmoji = (meal) => {
    if (meal.emoji) return meal.emoji;
    const type = (meal.type || '').toLowerCase();
    if (type.includes('colazione') || type.includes('breakfast') || type.includes('petit') || type.includes('café') || type.includes('frühstück') || type.includes('desayuno')) return '☀️';
    if (type.includes('spuntino') || type.includes('snack') || type.includes('collation') || type.includes('lanche')) return '🍎';
    if (type.includes('pranzo') || type.includes('lunch') || type.includes('déjeuner') || type.includes('almoço') || type.includes('almuerzo') || type.includes('mittag')) return '🍽️';
    if (type.includes('cena') || type.includes('dinner') || type.includes('dîner') || type.includes('jantar') || type.includes('abend')) return '🌙';
    return '🍴';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner"></div>
      </div>
    );
  }

  // Not premium - show upgrade prompt
  if (premiumLoaded && !isPremium) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-[#080D1A]' : 'bg-white'}`}>
        <div className={`fixed-header px-4 pt-4 pb-3 border-b ${isDark ? 'bg-[#080D1A] border-white/10' : 'bg-white border-gray-100'}`}>
          <div className="max-w-md mx-auto flex items-center gap-3">
            <button onClick={() => navigate(-1)} className={`p-2 rounded-full ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}>
              <ArrowLeft className={`w-5 h-5 ${isDark ? 'text-white' : 'text-gray-900'}`} />
            </button>
            <h1 className={`font-display text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('mealPlan.title')}
            </h1>
          </div>
        </div>

        <div className="content-below-header flex flex-col items-center justify-center min-h-[60vh] px-6">
          <div className={`w-20 h-20 rounded-3xl flex items-center justify-center mb-6 ${isDark ? 'bg-amber-500/20' : 'bg-amber-500/10'}`}>
            <Lock className="w-10 h-10 text-amber-500" />
          </div>
          <h2 className={`font-display text-xl mb-2 text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t('mealPlan.premiumFeature')}
          </h2>
          <p className={`text-center mb-6 max-w-xs ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
            {t('mealPlan.premiumDescription')}
          </p>
          <button
            onClick={() => navigate('/app/premium')}
            className="px-6 py-3 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold flex items-center gap-2"
            data-testid="meal-plan-upgrade-btn"
          >
            <Crown className="w-5 h-5" />
            {t('mealPlan.upgradePremium')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#080D1A]' : 'bg-white'}`} data-testid="meal-plan-page">
      {/* Header */}
      <div className={`fixed-header px-4 pt-4 pb-3 border-b ${isDark ? 'bg-[#080D1A] border-white/10' : 'bg-white border-gray-100'}`}>
        <div className="max-w-md mx-auto flex items-center gap-3">
          <button onClick={() => navigate(-1)} className={`p-2 rounded-full ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`} data-testid="meal-plan-back-btn">
            <ArrowLeft className={`w-5 h-5 ${isDark ? 'text-white' : 'text-gray-900'}`} />
          </button>
          <div className="flex-1">
            <h1 className={`font-display text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('mealPlan.title')}
            </h1>
            <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
              <Crown className="w-3 h-3 inline text-amber-500 mr-1" />
              {t('mealPlan.subtitle')}
            </p>
          </div>
          {hasPlan && planStatus === 'ready' && !showConfig && (
            <button
              onClick={handleEdit}
              className={`p-2 rounded-full ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
              data-testid="meal-plan-edit-btn"
            >
              <Pencil className={`w-5 h-5 ${isDark ? 'text-white/60' : 'text-gray-400'}`} />
            </button>
          )}
        </div>
      </div>

      <div className="content-below-header pb-6">
        {/* Generation form - show when no plan, or editing */}
        {(!hasPlan || showConfig || planStatus === 'failed') && !generating ? (
          <div className="px-4 pt-4">
            <div className="glass-card p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className={`font-display text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {hasPlan ? t('mealPlan.regenerate') : t('mealPlan.generateTitle')}
                  </h2>
                  <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                    {t('mealPlan.generateSubtitle')}
                  </p>
                </div>
              </div>

              {/* Allergies / Preferences */}
              <div className="mb-4">
                <label className={`text-xs font-medium uppercase mb-2 block ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                  {t('mealPlan.allergies')}
                </label>
                <input
                  type="text"
                  value={allergies}
                  onChange={(e) => setAllergies(e.target.value)}
                  placeholder={t('mealPlan.allergiesPlaceholder')}
                  className={`w-full px-4 py-3 rounded-xl text-sm ${
                    isDark ? 'bg-white/5 text-white placeholder-white/30 border border-white/10' : 'bg-gray-50 text-gray-900 placeholder-gray-400 border border-gray-200'
                  }`}
                  data-testid="meal-plan-allergies-input"
                />
              </div>

              <div className="mb-5">
                <label className={`text-xs font-medium uppercase mb-2 block ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                  {t('mealPlan.preferences')}
                </label>
                <input
                  type="text"
                  value={preferences}
                  onChange={(e) => setPreferences(e.target.value)}
                  placeholder={t('mealPlan.preferencesPlaceholder')}
                  className={`w-full px-4 py-3 rounded-xl text-sm ${
                    isDark ? 'bg-white/5 text-white placeholder-white/30 border border-white/10' : 'bg-gray-50 text-gray-900 placeholder-gray-400 border border-gray-200'
                  }`}
                  data-testid="meal-plan-preferences-input"
                />
              </div>

              {error && (
                <div className={`p-3 rounded-xl mb-4 flex items-center gap-2 ${isDark ? 'bg-red-500/10' : 'bg-red-50'}`}>
                  <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <p className="text-red-500 text-sm">{error}</p>
                </div>
              )}

              {/* Disclaimer with checkbox */}
              <div className={`p-3 rounded-xl mb-4 ${isDark ? 'bg-amber-500/10 border border-amber-500/15' : 'bg-amber-50 border border-amber-200'}`}>
                <div className="flex items-start gap-2 mb-2">
                  <AlertTriangle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
                  <p className={`text-xs leading-relaxed ${isDark ? 'text-amber-300/80' : 'text-amber-700'}`}>
                    {t('mealPlan.disclaimer')}
                  </p>
                </div>
                <label className="flex items-center gap-2.5 cursor-pointer pt-1" data-testid="meal-plan-disclaimer-checkbox">
                  <input
                    type="checkbox"
                    checked={disclaimerAccepted}
                    onChange={(e) => setDisclaimerAccepted(e.target.checked)}
                    className="w-4 h-4 rounded border-2 accent-emerald-500 flex-shrink-0"
                  />
                  <span className={`text-xs font-medium ${isDark ? 'text-white/70' : 'text-gray-700'}`}>
                    {t('mealPlan.disclaimerAccept')}
                  </span>
                </label>
              </div>

              <button
                onClick={handleGenerate}
                disabled={!disclaimerAccepted}
                className={`w-full py-4 rounded-2xl font-semibold flex items-center justify-center gap-2 transition-all ${
                  disclaimerAccepted
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white'
                    : isDark ? 'bg-white/10 text-white/30 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
                data-testid="meal-plan-generate-btn"
              >
                <Sparkles className="w-5 h-5" />
                {hasPlan ? t('mealPlan.regenerateBtn') : t('mealPlan.generateBtn')}
              </button>

              {showConfig && hasPlan && (
                <button
                  onClick={() => setShowConfig(false)}
                  className={`w-full mt-3 py-3 rounded-xl text-sm font-medium ${isDark ? 'bg-white/5 text-white/60' : 'bg-gray-100 text-gray-600'}`}
                >
                  {t('common.cancel')}
                </button>
              )}
            </div>
          </div>
        ) : null}

        {/* Generating state */}
        {generating && (
          <div className="px-4 pt-4">
            <div className="glass-card p-6 flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center mb-4">
                <Loader2 className="w-8 h-8 text-white animate-spin" />
              </div>
              <h3 className={`font-display text-lg mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Creazione del piano...
              </h3>
              <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                L'AI sta creando 21 piatti personalizzati per te
              </p>
              <GeneratingTimer />
            </div>
          </div>
        )}

        {/* Show plan */}
        {hasPlan && mealPlan && planStatus === 'ready' && !showConfig && (
          <div className="px-4 pt-4">
            {/* Language mismatch banner */}
            {planLanguage && planLanguage !== language && !generating && (
              <button
                onClick={handleGenerate}
                className={`w-full flex items-center gap-3 p-3 rounded-xl mb-4 ${isDark ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-amber-50 border border-amber-200'}`}
                data-testid="meal-plan-lang-mismatch-btn"
              >
                <Globe className="w-5 h-5 text-amber-500 flex-shrink-0" />
                <span className={`flex-1 text-left text-sm ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
                  {t('mealPlan.languageMismatch')}
                </span>
                <RefreshCw className="w-4 h-4 text-amber-500 flex-shrink-0" />
              </button>
            )}

            {/* Weekly Summary */}
            <div className="glass-card p-4 mb-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className={`font-display text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                    {t('mealPlan.weeklyAvg')}
                  </h3>
                  <div className="flex items-baseline gap-1 mt-1">
                    <Flame className="w-4 h-4 text-orange-500" />
                    <span className={`font-display text-2xl ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {(() => {
                        // Calculate real daily average from actual day data
                        const days = mealPlan.days || [];
                        if (days.length > 0) {
                          // Sum calories from each meal in each day
                          const total = days.reduce((sum, d) => {
                            // First check if day has pre-calculated total_calories
                            if (d.total_calories) return sum + d.total_calories;
                            // Otherwise sum meal calories
                            const dayTotal = (d.meals || []).reduce((mealSum, m) => mealSum + (m.calories || 0), 0);
                            return sum + dayTotal;
                          }, 0);
                          return Math.round(total / days.length);
                        }
                        // Fallback to weekly_calories divided by 7
                        if (mealPlan.weekly_calories) {
                          return Math.round(mealPlan.weekly_calories / 7);
                        }
                        return '~1500';
                      })()}
                    </span>
                    <span className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>kcal/{t('mealPlan.day')}</span>
                  </div>
                </div>
                <div className={`px-3 py-1.5 rounded-full text-xs font-medium ${isDark ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-500/10 text-emerald-600'}`}>
                  {t('mealPlan.balanced')}
                </div>
              </div>
            </div>

            {/* GLP-1 warning banner — highlighted in primary red when the
                CURRENT plan was generated with GLP-1 support enabled
                (snapshot on the plan doc). Toggling profile OFF and
                regenerating produces a plan without this flag → banner
                disappears. Non-medical. */}
            {planGlp1Support && (
              <div
                className="mb-4 rounded-2xl p-4 flex items-start gap-3 border-2 border-[#E8334A]"
                style={{
                  backgroundColor: 'rgba(232, 51, 74, 0.08)',
                  boxShadow: '0 0 0 3px rgba(232, 51, 74, 0.12)',
                }}
                data-testid="glp1-meal-plan-warning"
              >
                <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0 text-[#E8334A]" />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold text-[#E8334A]`}>
                    {t('mealPlan.glp1Title') || 'Piano adattato per terapia GLP-1'}
                  </p>
                  <p className={`text-xs mt-1 ${isDark ? 'text-white/70' : 'text-gray-700'}`}>
                    {t('mealPlan.glp1Warning') || 'Piano alimentare creato tenendo in considerazione la tua terapia GLP-1: porzioni moderate, alto contenuto proteico e ingredienti facilmente digeribili. Questo non è un consiglio medico: segui sempre le indicazioni del tuo medico o nutrizionista.'}
                  </p>
                </div>
              </div>
            )}

            {/* Shopping List Button */}
            <button
              onClick={() => navigate('/app/lista-spesa')}
              className={`w-full flex items-center gap-3 p-4 rounded-2xl mb-4 transition-all active:scale-[0.98] ${
                isDark ? 'bg-gradient-to-r from-amber-500/15 to-orange-500/15 border border-amber-500/20' : 'bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200'
              }`}
              data-testid="meal-plan-shopping-list-btn"
            >
              <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center flex-shrink-0">
                <ShoppingCart className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 text-left">
                <h3 className={`font-display text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {t('shoppingList.title')}
                </h3>
                <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                  {t('shoppingList.fromPlan')}
                </p>
              </div>
              <ShoppingCart className={`w-4 h-4 ${isDark ? 'text-amber-400' : 'text-amber-500'}`} />
            </button>

            {/* Days accordion */}
            <div className="space-y-3">
              {(mealPlan.days || []).map((day, dayIdx) => (
                <div
                  key={day.day || dayIdx}
                  className="glass-card overflow-hidden"
                  data-testid={`meal-plan-day-${dayIdx}`}
                >
                  {/* Day header */}
                  <button
                    onClick={() => setExpandedDay(expandedDay === dayIdx ? -1 : dayIdx)}
                    className="w-full flex items-center justify-between p-4"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-sm font-bold ${
                        expandedDay === dayIdx
                          ? 'bg-emerald-500 text-white'
                          : isDark ? 'bg-white/10 text-white/60' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {(day.day || '')[0]}
                      </div>
                      <div className="text-left">
                        <h3 className={`font-display text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {day.day}
                        </h3>
                        <p className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                          ~{day.total_calories || (day.meals || []).reduce((sum, m) => sum + (m.calories || 0), 0) || 1500} kcal
                        </p>
                      </div>
                    </div>
                    {expandedDay === dayIdx ? (
                      <ChevronUp className={`w-5 h-5 ${isDark ? 'text-white/40' : 'text-gray-400'}`} />
                    ) : (
                      <ChevronDown className={`w-5 h-5 ${isDark ? 'text-white/40' : 'text-gray-400'}`} />
                    )}
                  </button>

                  {/* Meals */}
                  {expandedDay === dayIdx && (
                    <div className={`px-4 pb-4 space-y-3 border-t ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                      {(day.meals || []).map((meal, mealIdx) => (
                        <div
                          key={meal.meal_id || meal.id || `${dayIdx}-${meal.type || meal.name || mealIdx}`}
                          className={`p-3 rounded-xl mt-3 ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}
                          data-testid={`meal-${dayIdx}-${mealIdx}`}
                        >
                          <div className="flex items-start gap-3">
                            <span className="text-xl flex-shrink-0 mt-0.5">{getMealEmoji(meal)}</span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <p className={`text-xs font-medium uppercase ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                                  {meal.type}
                                </p>
                                <span className={`text-xs font-medium ${isDark ? 'text-orange-400' : 'text-orange-600'}`}>
                                  {meal.calories} kcal
                                </span>
                              </div>
                              <h4 className={`font-medium text-sm mt-0.5 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                {meal.name}
                              </h4>
                              {meal.ingredients && meal.ingredients.length > 0 && (
                                <p className={`text-xs mt-1.5 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                                  {meal.ingredients.join(' · ')}
                                </p>
                              )}
                              {meal.preparation && (
                                <div className={`mt-2 pt-2 border-t ${isDark ? 'border-white/5' : 'border-gray-200'}`}>
                                  <div className="flex items-center gap-1 mb-1">
                                    <CookingPot className={`w-3 h-3 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
                                    <span className={`text-[10px] font-semibold uppercase ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                                      {t('mealPlan.preparation')}
                                    </span>
                                  </div>
                                  <p className={`text-xs leading-relaxed ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                                    {meal.preparation}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
